import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";

const BRAND_COLORS = {
  light: [
    { h: 262, s: 83, l: 50 },
    { h: 25, s: 95, l: 53 },
    { h: 45, s: 93, l: 47 },
  ],
  dark: [
    { h: 262, s: 83, l: 58 },
    { h: 25, s: 95, l: 60 },
    { h: 45, s: 93, l: 55 },
  ],
};

function hsl({ h, s, l }, alpha = 1) {
  return `hsla(${h}, ${s}%, ${l}%, ${alpha})`;
}

function rotateX([x, y, z], angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x, y * cos - z * sin, y * sin + z * cos];
}

function rotateY([x, y, z], angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x * cos + z * sin, y, -x * sin + z * cos];
}

function rotateZ([x, y, z], angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [x * cos - y * sin, x * sin + y * cos, z];
}

function project([x, y, z], width, height, fov = 640) {
  const scale = fov / (fov + z);
  return {
    x: x * scale + width / 2,
    y: y * scale + height / 2,
    scale,
  };
}

function createCube(size) {
  const s = size / 2;
  const vertices = [
    [-s, -s, -s],
    [s, -s, -s],
    [s, s, -s],
    [-s, s, -s],
    [-s, -s, s],
    [s, -s, s],
    [s, s, s],
    [-s, s, s],
  ];
  const edges = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  return { vertices, edges };
}

function createIcosahedron(radius) {
  const t = (1 + Math.sqrt(5)) / 2;
  const vertices = [
    [-1, t, 0],
    [1, t, 0],
    [-1, -t, 0],
    [1, -t, 0],
    [0, -1, t],
    [0, 1, t],
    [0, -1, -t],
    [0, 1, -t],
    [t, 0, -1],
    [t, 0, 1],
    [-t, 0, -1],
    [-t, 0, 1],
  ].map(([x, y, z]) => {
    const len = Math.sqrt(x * x + y * y + z * z);
    return [(x / len) * radius, (y / len) * radius, (z / len) * radius];
  });

  const edges = [
    [0, 1],
    [0, 5],
    [0, 7],
    [0, 10],
    [0, 11],
    [1, 5],
    [1, 7],
    [1, 8],
    [1, 9],
    [2, 3],
    [2, 4],
    [2, 6],
    [2, 10],
    [2, 11],
    [3, 4],
    [3, 6],
    [3, 8],
    [3, 9],
    [4, 5],
    [4, 9],
    [4, 11],
    [5, 9],
    [5, 11],
    [6, 7],
    [6, 8],
    [6, 10],
    [7, 8],
    [7, 10],
    [8, 9],
    [10, 11],
  ];

  return { vertices, edges };
}

function createTorus(major, minor, segments = 14) {
  const vertices = [];
  const edges = [];

  for (let i = 0; i < segments; i++) {
    const u = (i / segments) * Math.PI * 2;
    for (let j = 0; j < segments; j++) {
      const v = (j / segments) * Math.PI * 2;
      vertices.push([
        (major + minor * Math.cos(v)) * Math.cos(u),
        (major + minor * Math.cos(v)) * Math.sin(u),
        minor * Math.sin(v),
      ]);
    }
  }

  for (let i = 0; i < segments; i++) {
    for (let j = 0; j < segments; j++) {
      const current = i * segments + j;
      const nextU = ((i + 1) % segments) * segments + j;
      const nextV = i * segments + ((j + 1) % segments);
      edges.push([current, nextU], [current, nextV]);
    }
  }

  return { vertices, edges };
}

class WireframeObject {
  constructor({ geometry, color, position, scale = 1, rotationSpeed }) {
    this.geometry = geometry;
    this.color = color;
    this.position = position;
    this.scale = scale;
    this.rotationSpeed = rotationSpeed;
    this.rotation = {
      x: Math.random() * Math.PI,
      y: Math.random() * Math.PI,
      z: Math.random() * Math.PI,
    };
  }

  update(time, mouseOffset) {
    this.rotation.x += this.rotationSpeed.x;
    this.rotation.y += this.rotationSpeed.y;
    this.rotation.z += this.rotationSpeed.z;

    const parallaxX = mouseOffset.x * 0.02;
    const parallaxY = mouseOffset.y * 0.02;

    this.transformed = this.geometry.vertices.map((vertex) => {
      let point = [
        vertex[0] * this.scale,
        vertex[1] * this.scale,
        vertex[2] * this.scale,
      ];
      point = rotateX(point, this.rotation.x + parallaxY);
      point = rotateY(point, this.rotation.y + parallaxX);
      point = rotateZ(point, this.rotation.z);
      return [
        point[0] + this.position.x,
        point[1] + this.position.y,
        point[2] + this.position.z,
      ];
    });
  }

  draw(ctx, width, height, opacity) {
    const projected = this.transformed.map((point) =>
      project(point, width, height)
    );

    ctx.lineWidth = 1.2;
    this.geometry.edges.forEach(([a, b]) => {
      const p1 = projected[a];
      const p2 = projected[b];
      const depth = (this.transformed[a][2] + this.transformed[b][2]) / 2;
      const depthAlpha = Math.max(0.15, Math.min(1, 1 - (depth + 200) / 500));

      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = hsl(this.color, opacity * depthAlpha * 0.7);
      ctx.stroke();
    });

    projected.forEach((point, index) => {
      const depth = this.transformed[index][2];
      const depthAlpha = Math.max(0.2, Math.min(1, 1 - (depth + 200) / 500));
      const radius = 2.5 * point.scale;

      ctx.beginPath();
      ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = hsl(this.color, opacity * depthAlpha);
      ctx.fill();
    });
  }
}

export function Creative3DBackground() {
  const canvasRef = useRef(null);
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const mouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let startTime = performance.now();

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    const handleMouseMove = (event) => {
      mouseRef.current = {
        x: (event.clientX / window.innerWidth - 0.5) * 2,
        y: (event.clientY / window.innerHeight - 0.5) * 2,
      };
    };

    window.addEventListener("mousemove", handleMouseMove);

    const isDark = resolvedTheme === "dark";
    const colors = isDark ? BRAND_COLORS.dark : BRAND_COLORS.light;
    const opacity = isDark ? 0.55 : 0.45;

    const objects = [
      new WireframeObject({
        geometry: createIcosahedron(90),
        color: colors[0],
        position: { x: -220, y: -80, z: 0 },
        scale: 1.1,
        rotationSpeed: { x: 0.003, y: 0.004, z: 0.002 },
      }),
      new WireframeObject({
        geometry: createTorus(70, 28, 12),
        color: colors[1],
        position: { x: 240, y: 60, z: -60 },
        scale: 1,
        rotationSpeed: { x: 0.002, y: 0.005, z: 0.003 },
      }),
      new WireframeObject({
        geometry: createCube(110),
        color: colors[2],
        position: { x: 80, y: -140, z: 40 },
        scale: 1,
        rotationSpeed: { x: 0.004, y: 0.003, z: 0.002 },
      }),
      new WireframeObject({
        geometry: createIcosahedron(55),
        color: colors[1],
        position: { x: -160, y: 180, z: -30 },
        scale: 0.9,
        rotationSpeed: { x: 0.002, y: 0.003, z: 0.004 },
      }),
      new WireframeObject({
        geometry: createTorus(45, 16, 10),
        color: colors[2],
        position: { x: 300, y: -160, z: 20 },
        scale: 0.85,
        rotationSpeed: { x: 0.003, y: 0.002, z: 0.003 },
      }),
    ];

    const draw = (time) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const elapsed = prefersReducedMotion ? 0 : (time - startTime) * 0.001;

      objects.forEach((object, index) => {
        const offset = {
          x: mouseRef.current.x * (index % 2 === 0 ? 1 : -1),
          y: mouseRef.current.y * (index % 2 === 0 ? -1 : 1),
        };
        object.update(elapsed, offset);
        object.draw(ctx, canvas.width, canvas.height, opacity);
      });

      if (!prefersReducedMotion) {
        animationFrameId = requestAnimationFrame(draw);
      }
    };

    draw(startTime);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mounted, resolvedTheme]);

  if (!mounted) return null;

  const isDark = resolvedTheme === "dark";

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-0"
      aria-hidden="true"
      style={{ opacity: isDark ? 0.7 : 0.55 }}
    />
  );
}
